import { JhButton } from "designPackages/buttonPackage";
import { JhCheckbox } from "designPackages/checkboxPackage";
import { JhPopupDrawer } from "designPackages/popupPackage";
import { JhText } from "designPackages/textPackage";
import { Check } from "lucide-react";
import { useState } from "react";
import { STORAGE_KEYS } from "services/constants";
import { StorageService } from "services/StorageService";
import {
  WHATSAPP_BROADCAST_POPUP_STOPPED,
  WHATSAPP_BROADCAST_POPUP_TYPE
} from "services/waBroadCastConstants";
import useModalStore from "zustandStores/useModalStore";

const benefits: string[] = [
  "Save hours of manual messaging",
  "Easily customise templates",
  "Centralize candidate communications"
];

interface WhatsAppBroadcastProps {
  state: "open" | "close";
  closeCallBack: (source: string) => void;
  buttonText: string;
  onButtonClick: () => void;
  showDontShowAgain?: boolean;
  type: typeof WHATSAPP_BROADCAST_POPUP_TYPE;
}

export const WhatsAppBroadcastPopup = () => {
  const { modalData, setModalData } = useModalStore(state => ({
    modalData: state.modalData as WhatsAppBroadcastProps,
    setModalData: state.setModalData
  }));

  const {
    closeCallBack,
    buttonText,
    onButtonClick,
    showDontShowAgain,
    state,
    type
  } = modalData;

  const [dontShowAgain, setDontShowAgain] = useState<boolean | "indeterminate">(
    false
  );

  const handleDontShowAgainChange = (checked: boolean | "indeterminate") => {
    setDontShowAgain(checked);
    if (checked) {
      StorageService.setInStorage(
        WHATSAPP_BROADCAST_POPUP_STOPPED,
        "true",
        STORAGE_KEYS.SESSION_STORAGE
      );
      onClose("DONT_SHOW_AGAIN");
    } else {
      StorageService.removeFromStorage(
        WHATSAPP_BROADCAST_POPUP_STOPPED,
        STORAGE_KEYS.SESSION_STORAGE
      );
    }
  };

  const onClose = (source = "POPUP") => {
    setModalData({
      state: "close",
      type: WHATSAPP_BROADCAST_POPUP_TYPE
    });
    closeCallBack(source);
  };

  const handleButtonClick = () => {
    onButtonClick();
  };

  const isOpen = state === "open" && type === WHATSAPP_BROADCAST_POPUP_TYPE;
  return (
    <JhPopupDrawer
      position="center"
      open={isOpen}
      onClose={() => onClose("POPUP")}
    >
      <div className="p-6 max-w-md mx-auto bg-white rounded-lg tw-root">
        {/* WhatsApp Icon */}
        <div className="flex justify-center mb-1.5">
          <div className="p-4 bg-success-muted rounded-full flex items-center justify-center">
            <img src="/hire/bulk-wa-icon.svg" alt="Bulk Whatsapp icon" />
          </div>
        </div>

        {/* Title */}
        <JhText
          variant="caption1"
          color="discovery-foreground"
          className="text-center mb-1.5"
        >
          WhatsApp Broadcast
        </JhText>

        {/* Subtitle */}
        <JhText variant="emphasis2" className="text-center mb-5">
          Want to send WhatsApp messages in bulk?
        </JhText>

        {/* Features List */}
        <div className="space-y-1 mb-5">
          {benefits.map((benefit, index) => (
            <div key={index} className="flex items-center gap-2">
              <Check size={16} color="var(--success-foreground)" />
              <JhText variant="body2" color="muted-foreground">
                {benefit}
              </JhText>
            </div>
          ))}
        </div>

        {/* Action Button */}
        <div className={`mb-${showDontShowAgain ? "2.5" : "0"}`}>
          <JhButton onClick={handleButtonClick} className="w-full">
            {buttonText}
          </JhButton>
          <JhButton
            onClick={() => onClose("SEND_SEPERATELY")}
            variant="secondary"
            className="w-full mt-2.5"
          >
            Send it seperately
          </JhButton>
        </div>

        {/* Don't show again checkbox */}
        {showDontShowAgain && (
          <div className="flex items-center justify-center">
            <JhCheckbox
              id="dont-show-again"
              checked={dontShowAgain}
              onCheckedChange={val => handleDontShowAgainChange(val)}
            />
            <JhText
              htmlFor="dont-show-again"
              as="label"
              variant="body2"
              color="muted-foreground"
              className="ml-2"
            >
              Don't show me again
            </JhText>
          </div>
        )}
      </div>
    </JhPopupDrawer>
  );
};
